import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  LinkIcon,
  RefreshCwIcon,
  Trash2Icon,
  PencilIcon,
  XIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  CheckIcon,
  MinusIcon,
  BanIcon,
  Loader2Icon,
  LayoutGridIcon,
  ListIcon,
} from '../components/icons.js';
import { Icon } from '../components/Icon.js';
import type { ConfluenceSource, DocumentRegistryEntry } from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { CodeBlock } from '../components/CodeBlock.js';

/**
 * Phase 1.5 — Confluence Sources admin page.
 *
 * Andre registers a Confluence parent page; we crawl all descendants,
 * convert each to Markdown, and run them through the same ingest pipeline
 * the PDF/DOCX uploads use. Per-source ring placement (ring0 = internal,
 * ring1 = product). Manual sync only for now (Phase 2 = Cloud Scheduler).
 */

/**
 * Manual sync is now queued via a dedicated Cloud Run Job (2h timeout)
 * instead of running inline in the request — big subtrees used to die at
 * Cloud Run's 30 min service ceiling. The route returns 202 immediately;
 * the table polls the source row every 2.5s and renders progress from
 * `syncProgress` + `lastSyncStats` once the Job starts writing.
 */
interface SyncResponse {
  jobId: string;
  sourceId: string;
  status: 'queued';
}

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError) return err.message ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

function formatDate(iso?: string): string {
  if (!iso) return 'Never synced';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function statusBadge(status: ConfluenceSource['lastSyncStatus']): JSX.Element {
  // OneView trust/state mapping. The brief asked for idle/succeeded → success,
  // running/acquiring → tint, failed → error, stale → warning. Our schema only
  // has idle | running | success | partial | failed — `acquiring` and `stale`
  // don't exist here, so they're not mapped (flagged in commit body).
  const styles: Record<typeof status, string> = {
    idle: 'bg-nebula-gray-5 text-nebula-fg-3',
    running: 'bg-nebula-tint text-nebula-secondary',
    success: 'bg-nebula-success-bg text-nebula-success',
    partial: 'bg-nebula-warning-bg text-nebula-warning',
    failed: 'bg-nebula-error-bg text-nebula-error',
  };
  const labels: Record<typeof status, string> = {
    idle: 'Idle',
    running: 'Syncing',
    success: 'Succeeded',
    partial: 'Partial',
    failed: 'Failed',
  };
  return (
    <span
      className={`ov-meta-upper inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 ${styles[status]}`}
    >
      {status === 'running' && (
        <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-nebula-secondary" />
      )}
      {labels[status]}
    </span>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

function SyncProgressStrip({
  progress,
}: {
  progress: NonNullable<ConfluenceSource['syncProgress']>;
}): JSX.Element {
  const total = Math.max(progress.totalDescendants, 1);
  const pct = Math.min(100, Math.round((progress.currentIndex / total) * 100));
  const phaseLabel: Record<typeof progress.phase, string> = {
    listing: 'Listing',
    metadata: 'Metadata',
    fetching: 'Fetching',
    ingesting: 'Ingesting',
    completed: 'Completed',
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-xs text-nebula-fg-2">
        <span className="font-semibold text-nebula-fg-1">
          Syncing page {progress.currentIndex}/{progress.totalDescendants}
        </span>
        {progress.currentTitle && (
          <span className="truncate text-nebula-fg-3">— {truncate(progress.currentTitle, 60)}</span>
        )}
        <span className="ov-meta-upper ml-auto inline-flex items-center rounded-md bg-nebula-tint px-1.5 py-0.5 text-nebula-secondary">
          {phaseLabel[progress.phase]}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-nebula-tint">
        <div
          className="h-full bg-nebula-secondary transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="ov-meta text-nebula-fg-3">
        {progress.pagesSucceeded} succeeded · {progress.pagesUnchanged} unchanged ·{' '}
        {progress.pagesFailed} failed
      </div>
    </div>
  );
}

/**
 * Per-row poll. Fires GET /sources/:id every 2.5s while `enabled`. Localised
 * here (vs lifted to the parent) so only the running row re-renders — the
 * rest of the table stays stable. Inline-effect was tempting, but pulling it
 * into a hook makes the lifecycle (mount/unmount, enable flip) self-evident
 * and keeps SourceRow under 150 lines.
 */
function useSourcePoll(
  id: string,
  enabled: boolean,
  onUpdate: (source: ConfluenceSource) => void,
): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = (): void => {
      apiGet<{ source: ConfluenceSource }>(`/api/v1/admin/confluence/sources/${id}`)
        .then((res) => {
          if (!cancelled) onUpdate(res.source);
        })
        .catch(() => {
          /* transient — next tick retries. */
        });
    };
    const t = setInterval(tick, 2500);
    // Don't fire immediately — the parent's mounted fetch already has fresh
    // data, and an instant double-poll on every running flip is wasteful.
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [id, enabled, onUpdate]);
}

function relativeTime(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  if (diff < 1000) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

const PAGE_STATUS_LABEL: Record<
  NonNullable<NonNullable<ConfluenceSource['syncProgress']>['recentPages']>[number]['status'],
  string
> = {
  in_progress: 'In progress',
  succeeded: 'Succeeded',
  unchanged: 'Unchanged',
  failed: 'Failed',
  blocked: 'Blocked',
};

function PageStatusIcon({
  status,
}: {
  status: NonNullable<
    NonNullable<ConfluenceSource['syncProgress']>['recentPages']
  >[number]['status'];
}): JSX.Element {
  if (status === 'in_progress')
    return <Loader2Icon size={12} className="animate-spin text-nebula-secondary" />;
  if (status === 'succeeded') return <CheckIcon size={12} className="text-nebula-success" />;
  if (status === 'unchanged') return <MinusIcon size={12} className="text-nebula-fg-4" />;
  if (status === 'failed') return <XIcon size={12} className="text-nebula-error" />;
  return <BanIcon size={12} className="text-nebula-fg-4" />;
}

function LiveProgressLog({
  entries,
  defaultCollapsed,
}: {
  entries: NonNullable<NonNullable<ConfluenceSource['syncProgress']>['recentPages']>;
  defaultCollapsed: boolean;
}): JSX.Element | null {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // Tick once a second so "12s ago" stays current without re-fetching.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (collapsed) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [collapsed]);
  if (!entries || entries.length === 0) return null;
  // Newest first.
  const ordered = [...entries].reverse();
  return (
    <div className="mb-3 rounded-md border border-nebula-border-soft bg-nebula-gray-5 px-3 py-2">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="ov-meta-upper flex w-full items-center gap-1.5 text-nebula-fg-3 hover:text-nebula-fg-1"
      >
        {collapsed ? <ChevronRightIcon size={12} /> : <ChevronDownIcon size={12} />}
        Live progress (last {entries.length} {entries.length === 1 ? 'page' : 'pages'})
      </button>
      {!collapsed && (
        <ul className="mt-1.5 space-y-0.5">
          {ordered.map((e) => (
            <li key={`${e.pageId}-${e.ts}`} className="text-[12px] leading-snug">
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0" aria-label={PAGE_STATUS_LABEL[e.status]}>
                  <PageStatusIcon status={e.status} />
                </span>
                <span className="flex-1 truncate text-nebula-fg-1" title={e.title}>
                  {truncate(e.title || e.pageId, 80)}
                </span>
                <span className="ov-numeric flex-shrink-0 text-[11px] text-nebula-fg-4">
                  {relativeTime(e.ts, now)}
                  {typeof e.durationMs === 'number' && e.status !== 'in_progress'
                    ? ` · +${e.durationMs}ms`
                    : ''}
                </span>
              </div>
              {e.errorMessage && (
                <div
                  className="ml-5 truncate font-mono text-[11px] text-nebula-error"
                  title={e.errorMessage}
                >
                  {truncate(e.errorMessage, 200)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface SourceCardProps {
  source: ConfluenceSource;
  expanded: boolean;
  syncing: boolean;
  onToggleExpand: (id: string) => void;
  onSync: (id: string, force?: boolean) => void;
  onRequestReingest: (s: ConfluenceSource) => void;
  onRequestDelete: (s: ConfluenceSource) => void;
  onSourceUpdate: (s: ConfluenceSource) => void;
  onError: (msg: string) => void;
}

const SourceCard = memo(function SourceCard({
  source: s,
  expanded,
  syncing,
  onToggleExpand,
  onSync,
  onRequestReingest,
  onRequestDelete,
  onSourceUpdate,
  onError,
}: SourceCardProps): JSX.Element {
  useSourcePoll(s.id, s.lastSyncStatus === 'running', onSourceUpdate);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the kebab popover on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (ev: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const stats = s.lastSyncStats;
  const ringLabel = s.ring === 'ring0' ? 'OneNebula Internal' : 'Product';
  const ringChipClass =
    s.ring === 'ring0'
      ? 'bg-nebula-gray-5 text-nebula-fg-1'
      : 'bg-nebula-tint text-nebula-secondary';
  // True lifetime indexed count from the backend. The last-run sum
  // (`pagesSucceeded + pagesUnchanged`) caps at 250 for delta syncs and
  // is 0 when nothing changed — neither matches "how many pages are
  // actually indexed for this source." Falls back to the legacy sum so
  // old client builds against the pre-fix backend don't render "—".
  const pageCountHint =
    (s as { indexedCount?: number }).indexedCount ??
    (stats?.pagesSucceeded ?? 0) + (stats?.pagesUnchanged ?? 0);
  const recentPages = s.syncProgress?.recentPages ?? [];
  const terminal =
    s.lastSyncStatus === 'success' ||
    s.lastSyncStatus === 'partial' ||
    s.lastSyncStatus === 'failed';
  const now = Date.now();
  const lastSyncRel = s.lastSyncedAt ? relativeTime(s.lastSyncedAt, now) : null;
  // Mini recent-pages list while running — show the latest 3 entries.
  const miniRecent =
    s.lastSyncStatus === 'running' && recentPages.length > 0
      ? [...recentPages].reverse().slice(0, 3)
      : [];

  return (
    <div className="flex flex-col rounded-md border border-nebula-border-soft bg-nebula-surface p-5 transition-shadow hover:shadow-sm">
      {/* Top row: icon + name + status chip */}
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-nebula-tint text-nebula-secondary">
          <LinkIcon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="ov-h-card truncate text-nebula-fg-1" title={s.name}>
            {s.name}
          </h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span
              className={`ov-meta-upper inline-flex items-center rounded-md px-1.5 py-0.5 ${ringChipClass}`}
            >
              {ringLabel}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0">{statusBadge(s.lastSyncStatus)}</div>
      </div>

      {/* Meta row */}
      <div className="ov-meta mt-3 text-nebula-fg-3">
        <span className="font-mono text-[12px] text-nebula-fg-2">{s.cloudBaseUrl}</span>
        {' · '}Homepage:{' '}
        <a
          href={confluenceUrl(s.cloudBaseUrl, s.rootPageId)}
          target="_blank"
          rel="noopener noreferrer"
          title={confluenceUrl(s.cloudBaseUrl, s.rootPageId)}
          className="inline-flex items-center gap-1 font-mono text-[12px] text-nebula-secondary hover:underline"
        >
          {s.rootPageId}
          <ExternalLinkIcon size={11} />
        </a>
        {pageCountHint > 0 && (
          <>
            {' · '}
            <span className="ov-numeric text-nebula-fg-2">{pageCountHint}</span> pages
          </>
        )}
      </div>

      {/* Last sync row */}
      <div className="ov-meta mt-1 text-nebula-fg-3">
        {lastSyncRel ? `Last sync ${lastSyncRel}` : 'Never synced'}
        {stats && (
          <>
            {' · '}
            {stats.pagesSucceeded} new
            {stats.pagesUnchanged > 0 ? ` · ${stats.pagesUnchanged} unchanged` : ''}
            {stats.pagesFailed > 0 ? ` · ${stats.pagesFailed} failed` : ''}
          </>
        )}
      </div>

      {/* Live progress while running */}
      {s.lastSyncStatus === 'running' && s.syncProgress && (
        <div className="mt-4 rounded-md border border-nebula-border-soft bg-nebula-tint/40 px-3 py-2.5">
          <SyncProgressStrip progress={s.syncProgress} />
          {miniRecent.length > 0 && (
            <ul className="mt-2 space-y-0.5 border-t border-nebula-border-soft pt-2">
              {miniRecent.map((e) => (
                <li
                  key={`${e.pageId}-${e.ts}`}
                  className="flex items-center gap-2 text-[12px] leading-snug"
                >
                  <span className="flex-shrink-0" aria-label={PAGE_STATUS_LABEL[e.status]}>
                    <PageStatusIcon status={e.status} />
                  </span>
                  <span className="flex-1 truncate text-nebula-fg-1" title={e.title}>
                    {truncate(e.title || e.pageId, 70)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/*
        Bottom action row. Buttons are tight on xl:grid-cols-3 (~470px card)
        so we use compact labels ("Re-ingest" not "Force re-ingest"), drop
        the icon on the secondary action, and `whitespace-nowrap` the
        "View pages" link so it never wraps to two lines.
      */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onToggleExpand(s.id)}
          className="ov-meta inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-nebula-secondary hover:text-nebula-primary"
        >
          {expanded ? <ChevronDownIcon size={13} /> : <ChevronRightIcon size={13} />}
          {expanded ? 'Hide pages' : `View pages${pageCountHint ? ` (${pageCountHint})` : ''}`}
        </button>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => onSync(s.id)}
            disabled={syncing}
            title="Walk the subtree and only re-ingest pages whose Confluence version has changed since the last sync. Fast on follow-up runs."
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1.5 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
          >
            <RefreshCwIcon size={13} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync'}
          </button>
          <button
            type="button"
            onClick={() => onRequestReingest(s)}
            disabled={syncing}
            title="Re-ingest the source. Choose whether to skip pages that haven't changed."
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1.5 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
          >
            Re-ingest
          </button>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-nebula-border bg-nebula-surface text-nebula-fg-2 hover:bg-nebula-tint"
            >
              <Icon name="more_vert" size={16} />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-md border border-nebula-border-soft bg-nebula-surface shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onToggleExpand(s.id);
                  }}
                  className="ov-body flex w-full items-center gap-2 px-3 py-2 text-left text-nebula-fg-1 hover:bg-nebula-tint"
                >
                  <PencilIcon size={14} />
                  View details
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onRequestDelete(s);
                  }}
                  className="ov-body flex w-full items-center gap-2 px-3 py-2 text-left text-nebula-error hover:bg-nebula-error-bg"
                >
                  <Trash2Icon size={14} />
                  Delete source
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Collapsible details: live log + synced-pages panel */}
      {expanded && (
        <div className="mt-4 border-t border-nebula-border-soft pt-4">
          {recentPages.length > 0 && (
            <LiveProgressLog entries={recentPages} defaultCollapsed={terminal} />
          )}
          <SourcePagesPanel source={s} onError={onError} />
        </div>
      )}
    </div>
  );
});

/**
 * Dense list view for Confluence sources. Same data + same action handlers
 * as the SourceCard, just packed into a table for users who'd rather scan a
 * lot of sources at once than scroll a grid of cards.
 */
function ConfluenceSourceList({
  sources,
  syncing,
  onSync,
  onRequestReingest,
  onRequestDelete,
}: {
  sources: ConfluenceSource[];
  syncing: Record<string, boolean>;
  onSync: (id: string) => void;
  onRequestReingest: (src: ConfluenceSource) => void;
  onRequestDelete: (src: ConfluenceSource) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-nebula-border-soft bg-nebula-surface">
      <table className="w-full min-w-[760px] text-sm">
        <thead className="border-b border-nebula-border bg-nebula-gray-6">
          <tr>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Source</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Homepage</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Pages</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Status</th>
            <th className="ov-meta-upper px-3 py-2 text-right text-nebula-fg-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => {
            const stats = s.lastSyncStats;
            // True lifetime indexed count from the backend. The last-run sum
            // (`pagesSucceeded + pagesUnchanged`) caps at 250 for delta syncs and
            // is 0 when nothing changed — neither matches "how many pages are
            // actually indexed for this source." Falls back to the legacy sum so
            // old client builds against the pre-fix backend don't render "—".
            const pageCountHint =
              (s as { indexedCount?: number }).indexedCount ??
              (stats?.pagesSucceeded ?? 0) + (stats?.pagesUnchanged ?? 0);
            const isSyncing = !!syncing[s.id];
            return (
              <tr
                key={s.id}
                className="border-b border-nebula-border-soft last:border-b-0 hover:bg-nebula-tint"
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-nebula-fg-1" title={s.name}>
                    {s.name}
                  </div>
                  <div className="ov-meta truncate font-mono text-[11px] text-nebula-fg-3">
                    {s.cloudBaseUrl}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-[12px] text-nebula-fg-2">
                  <a
                    href={confluenceUrl(s.cloudBaseUrl, s.rootPageId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={confluenceUrl(s.cloudBaseUrl, s.rootPageId)}
                    className="inline-flex items-center gap-1 text-nebula-secondary hover:underline"
                  >
                    {s.rootPageId}
                    <ExternalLinkIcon size={11} />
                  </a>
                </td>
                <td className="ov-numeric px-3 py-2 text-nebula-fg-1">
                  {pageCountHint > 0 ? pageCountHint.toLocaleString('en-GB') : '—'}
                </td>
                <td className="px-3 py-2">{statusBadge(s.lastSyncStatus)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => onSync(s.id)}
                      disabled={isSyncing}
                      title="Sync delta"
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1 text-xs font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
                    >
                      <RefreshCwIcon size={12} className={isSyncing ? 'animate-spin' : ''} />
                      Sync
                    </button>
                    <button
                      type="button"
                      onClick={() => onRequestReingest(s)}
                      disabled={isSyncing}
                      title="Re-ingest"
                      className="inline-flex items-center whitespace-nowrap rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1 text-xs font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
                    >
                      Re-ingest
                    </button>
                    <button
                      type="button"
                      onClick={() => onRequestDelete(s)}
                      title="Delete source"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-nebula-border bg-nebula-surface text-nebula-error hover:bg-nebula-error-bg"
                    >
                      <Trash2Icon size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function AdminConfluencePage(): JSX.Element {
  const [sources, setSources] = useState<ConfluenceSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [pendingDelete, setPendingDelete] = useState<ConfluenceSource | null>(null);
  // Reingest modal — replaces the bare confirm() prompt. Lets the operator
  // explicitly toggle "skip pages where the Confluence version hasn't changed"
  // (delta mode = checked, default) vs full force-reingest (checked = off).
  const [reingestTarget, setReingestTarget] = useState<ConfluenceSource | null>(null);
  const [reingestSkipUnchanged, setReingestSkipUnchanged] = useState<boolean>(true);
  const [reload, setReload] = useState(0);
  // View toggle (cards / list). Persisted to localStorage so a power user
  // doesn't have to flip it on every page load. Defaults to cards.
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    if (typeof window === 'undefined') return 'cards';
    const stored = window.localStorage.getItem('kb.confluence.viewMode');
    return stored === 'list' || stored === 'cards' ? stored : 'cards';
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('kb.confluence.viewMode', viewMode);
    } catch {
      // localStorage can throw in private modes / disabled storage; ignore.
    }
  }, [viewMode]);
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});

  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const toggleExpanded = useCallback(
    (id: string) => setExpandedSources((s) => ({ ...s, [id]: !s[id] })),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTopError(null);
    apiGet<{ sources: ConfluenceSource[] }>('/api/v1/admin/confluence/sources')
      .then((res) => {
        if (!cancelled) setSources(res.sources);
      })
      .catch((err: unknown) => {
        if (!cancelled) setTopError(errorMessage(err, 'Could not load Confluence sources.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  // Per-row replace by id. Used by useSourcePoll so a single running source
  // only re-renders its own <SourceRow>, not the entire table.
  const replaceSource = useCallback((next: ConfluenceSource) => {
    setSources((prev) => {
      const idx = prev.findIndex((s) => s.id === next.id);
      if (idx < 0) return prev;
      if (prev[idx] === next) return prev;
      const copy = prev.slice();
      copy[idx] = next;
      return copy;
    });
  }, []);

  const onSync = useCallback(
    async (id: string, force = false) => {
      setSyncing((s) => ({ ...s, [id]: true }));
      try {
        const url = force
          ? `/api/v1/admin/confluence/sources/${id}/sync?force=true`
          : `/api/v1/admin/confluence/sources/${id}/sync`;
        await apiPost<SyncResponse>(url, {});
        alert(`Sync queued${force ? ' (forced)' : ''} — track progress on the row.`);
        // Refetch this one row so its lastSyncStatus flips to "running" and
        // useSourcePoll engages without waiting for the next full list reload.
        apiGet<{ source: ConfluenceSource }>(`/api/v1/admin/confluence/sources/${id}`)
          .then((res) => replaceSource(res.source))
          .catch(() => undefined);
      } catch (err) {
        setTopError(errorMessage(err, 'Sync failed.'));
      } finally {
        setSyncing((s) => ({ ...s, [id]: false }));
      }
    },
    [replaceSource],
  );

  const onDelete = useCallback(
    async (source: ConfluenceSource) => {
      try {
        await apiDelete(`/api/v1/admin/confluence/sources/${source.id}`);
        setPendingDelete(null);
        refresh();
      } catch (err) {
        setTopError(errorMessage(err, 'Delete failed.'));
      }
    },
    [refresh],
  );

  // KPI derivations — all from existing state, no new API calls.
  // `pageCount` proxy = succeeded + unchanged from lastSyncStats (the
  // registry-row count isn't fetched at the list level). Active syncs
  // counts sources whose status is `running` — `acquiring` isn't a state
  // in our schema, so it's not contributing here (flagged in commit body).
  const kpis = useMemo(() => {
    const totalSources = sources.length;
    let totalPages = 0;
    let activeSyncs = 0;
    for (const s of sources) {
      const stats = s.lastSyncStats;
      const indexed = (s as { indexedCount?: number }).indexedCount;
      totalPages +=
        typeof indexed === 'number'
          ? indexed
          : (stats?.pagesSucceeded ?? 0) + (stats?.pagesUnchanged ?? 0);
      if (s.lastSyncStatus === 'running') activeSyncs += 1;
    }
    return { totalSources, totalPages, activeSyncs };
  }, [sources]);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="ov-h-page">Confluence Sources</h1>
            <p className="ov-body mt-1 text-nebula-fg-3">
              Sync Confluence spaces and pages into the knowledgebase.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div
              role="group"
              aria-label="View mode"
              className="inline-flex overflow-hidden rounded-md border border-nebula-border bg-nebula-surface"
            >
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                aria-pressed={viewMode === 'cards'}
                title="Card view"
                className={`inline-flex h-9 w-9 items-center justify-center transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-nebula-primary text-white'
                    : 'text-nebula-fg-2 hover:bg-nebula-tint'
                }`}
              >
                <LayoutGridIcon size={16} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                title="List view"
                className={`inline-flex h-9 w-9 items-center justify-center border-l border-nebula-border transition-colors ${
                  viewMode === 'list'
                    ? 'bg-nebula-primary text-white'
                    : 'text-nebula-fg-2 hover:bg-nebula-tint'
                }`}
              >
                <ListIcon size={16} />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90"
            >
              Connect new space
            </button>
          </div>
        </header>

        {/*
          KPI tiles — em-dash placeholders while the sources list is still
          loading so the tiles don't flash a misleading "0 / No sources yet"
          before the API resolves.
        */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiTile
            label="Total sources"
            value={
              loading && sources.length === 0 ? '—' : kpis.totalSources.toLocaleString('en-GB')
            }
            sub={
              loading && sources.length === 0
                ? undefined
                : kpis.totalSources === 0
                  ? 'No sources yet'
                  : undefined
            }
          />
          <KpiTile
            label="Total pages"
            value={
              loading && sources.length === 0
                ? '—'
                : kpis.totalPages > 0
                  ? kpis.totalPages.toLocaleString('en-GB')
                  : '—'
            }
            sub={
              !loading && kpis.totalSources > 0
                ? `${Math.round(kpis.totalPages / Math.max(1, kpis.totalSources)).toLocaleString('en-GB')} avg / source`
                : undefined
            }
          />
          <KpiTile
            label="Active syncs"
            value={loading && sources.length === 0 ? '—' : kpis.activeSyncs.toLocaleString('en-GB')}
            sub={
              loading && sources.length === 0
                ? undefined
                : kpis.activeSyncs > 0
                  ? 'Syncing now'
                  : 'Idle'
            }
          />
        </div>

        {topError && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-nebula-error/20 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
            <span>{topError}</span>
            <button
              type="button"
              onClick={() => setTopError(null)}
              aria-label="Close error message"
              className="text-nebula-error/60 hover:text-nebula-error"
            >
              <XIcon size={14} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="ov-body text-nebula-fg-3">Loading…</div>
        ) : sources.length === 0 ? (
          <div className="rounded-md border border-dashed border-nebula-border bg-nebula-surface p-10 text-center">
            <LinkIcon size={28} className="mx-auto text-nebula-fg-4" />
            <p className="ov-body mt-3 text-nebula-fg-3">
              No Confluence sources configured. Click &quot;Connect new space&quot; to start.
            </p>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sources.map((s) => (
              <SourceCard
                key={s.id}
                source={s}
                expanded={!!expandedSources[s.id]}
                syncing={!!syncing[s.id]}
                onToggleExpand={toggleExpanded}
                onSync={onSync}
                onRequestReingest={(src) => {
                  setReingestSkipUnchanged(true);
                  setReingestTarget(src);
                }}
                onRequestDelete={setPendingDelete}
                onSourceUpdate={replaceSource}
                onError={setTopError}
              />
            ))}
          </div>
        ) : (
          <ConfluenceSourceList
            sources={sources}
            syncing={syncing}
            onSync={onSync}
            onRequestReingest={(src) => {
              setReingestSkipUnchanged(true);
              setReingestTarget(src);
            }}
            onRequestDelete={setPendingDelete}
          />
        )}
      </div>

      {showAdd && (
        <AddSourceModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            refresh();
          }}
          onError={(msg) => setTopError(msg)}
        />
      )}

      {reingestTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
          <div className="w-full max-w-md rounded-md bg-nebula-surface p-6 shadow-lg">
            <h2 className="ov-h-section">Re-ingest source</h2>
            <p className="ov-body mt-2 text-nebula-fg-3">
              &quot;{reingestTarget.name}&quot; — choose how to handle pages already in the
              knowledgebase.
            </p>
            <label className="mt-4 flex items-start gap-3 rounded-md border border-nebula-border-soft bg-nebula-tint/40 p-3 text-sm">
              <input
                type="checkbox"
                checked={reingestSkipUnchanged}
                onChange={(e) => setReingestSkipUnchanged(e.target.checked)}
                className="mt-0.5"
              />
              <span className="flex-1">
                <span className="font-semibold text-nebula-fg-1">
                  Skip pages where the Confluence version hasn&apos;t changed
                </span>
                <br />
                <span className="text-nebula-fg-3">
                  Recommended. Walks the subtree, but only re-renders Markdown and re-embeds chunks
                  for pages whose Confluence <code>version.number</code> moved since the last sync.
                  Fast on follow-up runs.
                </span>
              </span>
            </label>
            <p className="ov-meta mt-3 text-nebula-fg-3">
              Uncheck for a <strong>full force re-ingest</strong> — every page in the subtree is
              re-rendered and re-embedded. Use after a rendering improvement ships or when you
              suspect drift.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReingestTarget(null)}
                className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const target = reingestTarget;
                  setReingestTarget(null);
                  void onSync(target.id, !reingestSkipUnchanged);
                }}
                className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90"
              >
                {reingestSkipUnchanged ? 'Re-ingest (skip unchanged)' : 'Force re-ingest all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
          <div className="w-full max-w-md rounded-md bg-nebula-surface p-6 shadow-lg">
            <h2 className="ov-h-section">Delete source?</h2>
            <p className="ov-body mt-2 text-nebula-fg-3">
              Removes the registration for &quot;{pendingDelete.name}&quot;. Already-ingested pages
              stay in the knowledgebase — delete those individually if needed.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onDelete(pendingDelete)}
                className="rounded-md bg-nebula-error px-4 py-2 font-ui text-white hover:bg-nebula-error/90"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

interface AddSourceModalProps {
  onClose: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
}

function AddSourceModal({ onClose, onCreated, onError }: AddSourceModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [cloudBaseUrl, setCloudBaseUrl] = useState('onenebula.atlassian.net');
  const [rootPageId, setRootPageId] = useState('');
  const [ring, setRing] = useState<'ring0' | 'ring1'>('ring1');
  const [defaultClassification, setDefaultClassification] = useState<
    'public' | 'internal' | 'confidential' | 'restricted'
  >('internal');
  const [defaultAccessGroups, setDefaultAccessGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const requiresGroup =
    defaultClassification === 'confidential' || defaultClassification === 'restricted';
  const groupError = requiresGroup && defaultAccessGroups.length === 0;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim() || !rootPageId.trim()) return;
    if (groupError) {
      onError('Confidential or Restricted classification requires at least one access group.');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/api/v1/admin/confluence/sources', {
        name: name.trim(),
        cloudBaseUrl: cloudBaseUrl.trim(),
        rootPageId: rootPageId.trim(),
        ring,
        ...(defaultClassification !== 'internal' ? { defaultClassification } : {}),
        ...(defaultAccessGroups.length > 0 ? { defaultAccessGroups } : {}),
      });
      onCreated();
    } catch (err) {
      onError(errorMessage(err, 'Could not create source.'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none focus:ring-2 focus:ring-nebula-secondary/20';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-md bg-nebula-surface p-6 shadow-lg"
      >
        <div className="flex items-start justify-between">
          <h2 className="ov-h-section">Connect new space</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-tint"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cf-name">
              Name
            </label>
            <input
              id="cf-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="HR Handbook"
            />
          </div>
          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cf-host">
              Cloud host
            </label>
            <input
              id="cf-host"
              required
              value={cloudBaseUrl}
              onChange={(e) => setCloudBaseUrl(e.target.value)}
              className={inputCls}
              placeholder="onenebula.atlassian.net"
            />
            <p className="ov-meta mt-1 text-nebula-fg-3">No protocol, no trailing slash.</p>
          </div>
          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cf-root">
              Root page ID
            </label>
            <input
              id="cf-root"
              required
              inputMode="numeric"
              pattern="\d+"
              value={rootPageId}
              onChange={(e) => setRootPageId(e.target.value)}
              className={`${inputCls} font-mono`}
              placeholder="1234567890"
            />
            <p className="ov-meta mt-1 text-nebula-fg-3">
              Find the ID in the page URL: <code>…/pages/&#123;id&#125;/Page-Title</code>
            </p>
          </div>
          <fieldset>
            <legend className="ov-meta-upper text-nebula-fg-2">Ring</legend>
            <div className="mt-2 space-y-2">
              <label className="ov-body flex items-start gap-2 text-nebula-fg-1">
                <input
                  type="radio"
                  name="ring"
                  checked={ring === 'ring1'}
                  onChange={() => setRing('ring1')}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">Product</span> (ring1) — broader access
                </span>
              </label>
              <label className="ov-body flex items-start gap-2 text-nebula-fg-1">
                <input
                  type="radio"
                  name="ring"
                  checked={ring === 'ring0'}
                  onChange={() => setRing('ring0')}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">Internal</span> (ring0) — internal staff only
                </span>
              </label>
            </div>
          </fieldset>
          <fieldset>
            <legend className="ov-meta-upper text-nebula-fg-2">Default classification</legend>
            <p className="ov-meta mt-1 text-nebula-fg-3">
              Cascaded onto every doc ingested from this source. Operators can downgrade per-doc
              later from /documenten.
            </p>
            <select
              value={defaultClassification}
              onChange={(e) =>
                setDefaultClassification(
                  e.target.value as 'public' | 'internal' | 'confidential' | 'restricted',
                )
              }
              className={inputCls}
            >
              <option value="public">public</option>
              <option value="internal">internal</option>
              <option value="confidential">confidential</option>
              <option value="restricted">restricted</option>
            </select>
            {requiresGroup && (
              <label className="ov-body mt-2 flex items-center gap-2 text-nebula-fg-1">
                <input
                  type="checkbox"
                  checked={defaultAccessGroups.includes('confidential')}
                  onChange={(e) =>
                    setDefaultAccessGroups(
                      e.target.checked
                        ? Array.from(new Set([...defaultAccessGroups, 'confidential']))
                        : defaultAccessGroups.filter((g) => g !== 'confidential'),
                    )
                  }
                />
                <span>Access group: confidential (KB-Confidential members)</span>
              </label>
            )}
            {groupError && (
              <p className="ov-meta mt-1 text-nebula-error">
                Confidential or Restricted requires at least one access group.
              </p>
            )}
          </fieldset>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90 disabled:opacity-60"
          >
            {submitting ? 'Creating…' : 'Create source'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- KPI tile -------------------------------------------------------
// Inline single-use OneView tile, mirrors Phase 4 AdminKnowledgebase styling.

function KpiTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-nebula-border-soft bg-nebula-surface p-4 transition-shadow hover:shadow-sm">
      <div className="ov-meta-upper text-nebula-fg-3">{label}</div>
      <div className="ov-numeric mt-2 text-3xl text-nebula-fg-1">{value}</div>
      {sub && <div className="ov-meta mt-1 text-nebula-fg-3">{sub}</div>}
    </div>
  );
}

interface SourcePagesPanelProps {
  source: ConfluenceSource;
  onError: (msg: string) => void;
}

interface PagesResponse {
  source: ConfluenceSource;
  pages: DocumentRegistryEntry[];
  blockedPages?: DocumentRegistryEntry[];
  totalChunks: number;
}

interface BackfillResponse {
  stamped: number;
  alreadyTagged: number;
  notIngested: number;
}

interface RefreshMetadataResponse {
  updated: number;
  skippedUnchanged: number;
  notIngested: number;
  errors: { pageId: string; error: string }[];
}

function pageIdFromFilename(filename: string): string | null {
  // confluence-{pageId}-{slug}.md — pageId is digits.
  const m = /^confluence-(\d+)-/.exec(filename);
  return m ? (m[1] ?? null) : null;
}

function titleFromFilename(filename: string): string {
  // Strip the `confluence-{pageId}-` prefix and `.md` suffix, undo slug
  // hyphens for display.
  const m = /^confluence-\d+-(.+)\.md$/.exec(filename);
  if (!m || !m[1]) return filename;
  return m[1].replace(/-/g, ' ');
}

function confluenceUrl(cloudBaseUrl: string, pageId: string): string {
  // viewpage.action works regardless of which space the page lives in,
  // so we don't need to know the space key per page.
  return `https://${cloudBaseUrl}/wiki/pages/viewpage.action?pageId=${pageId}`;
}

function SourcePagesPanel({ source, onError }: SourcePagesPanelProps): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState<DocumentRegistryEntry[] | null>(null);
  const [blockedPages, setBlockedPages] = useState<DocumentRegistryEntry[]>([]);
  const [totalChunks, setTotalChunks] = useState(0);
  const [reindexing, setReindexing] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [unblocking, setUnblocking] = useState<Record<string, boolean>>({});
  const [pendingDelete, setPendingDelete] = useState<{
    filename: string;
    title: string;
  } | null>(null);
  const [inspectChunks, setInspectChunks] = useState<DocumentRegistryEntry | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [refreshingMetadata, setRefreshingMetadata] = useState(false);
  const [reload, setReload] = useState(0);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<PagesResponse>(`/api/v1/admin/confluence/sources/${source.id}/pages`)
      .then((res) => {
        if (cancelled) return;
        setPages(res.pages);
        setBlockedPages(res.blockedPages ?? []);
        setTotalChunks(res.totalChunks);
      })
      .catch((err: unknown) => {
        if (!cancelled) onError(errorMessage(err, 'Could not load pages.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source.id, reload, onError]);

  const onReindex = useCallback(
    async (filename: string) => {
      const pageId = pageIdFromFilename(filename);
      if (!pageId) {
        onError('Could not derive Confluence pageId from filename — re-sync skipped.');
        return;
      }
      setReindexing((s) => ({ ...s, [filename]: true }));
      try {
        // Per-page Re-sync re-fetches from Confluence (source of truth)
        // rather than re-chunking the cached .md from the canonical bucket
        // — see /admin/confluence/sources/:id/pages/:pageId/resync.
        await apiPost(`/api/v1/admin/confluence/sources/${source.id}/pages/${pageId}/resync`, {});
        // Job runs synchronously to terminal status server-side — refresh
        // immediately so the new lastIngestedAt / chunkCount appear.
        refresh();
      } catch (err) {
        onError(errorMessage(err, 'Re-sync failed.'));
      } finally {
        setReindexing((s) => ({ ...s, [filename]: false }));
      }
    },
    [source.id, refresh, onError],
  );

  const performDelete = useCallback(
    async (filename: string, blockFromIngest: boolean) => {
      setDeleting((s) => ({ ...s, [filename]: true }));
      try {
        await apiDelete(`/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}`, {
          confirmFilename: filename,
          blockFromIngest,
        });
        setPendingDelete(null);
        refresh();
      } catch (err) {
        onError(errorMessage(err, 'Delete failed.'));
      } finally {
        setDeleting((s) => ({ ...s, [filename]: false }));
      }
    },
    [refresh, onError],
  );

  const onUnblock = useCallback(
    async (filename: string) => {
      setUnblocking((s) => ({ ...s, [filename]: true }));
      // Optimistic: drop from blocked list immediately. Roll back on error.
      const prev = blockedPages;
      setBlockedPages((bp) => bp.filter((p) => p.filename !== filename));
      try {
        await apiPost(
          `/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}/unblock`,
          {},
        );
        refresh();
      } catch (err) {
        setBlockedPages(prev);
        onError(errorMessage(err, 'Unblock failed.'));
      } finally {
        setUnblocking((s) => ({ ...s, [filename]: false }));
      }
    },
    [blockedPages, refresh, onError],
  );

  const onBackfill = useCallback(async () => {
    setBackfilling(true);
    try {
      const res = await apiPost<BackfillResponse>(
        `/api/v1/admin/confluence/sources/${source.id}/backfill-source-id`,
        {},
      );
      alert(
        `Backfill complete: ${res.stamped} stamped, ${res.alreadyTagged} already tagged, ${res.notIngested} not yet ingested.`,
      );
      refresh();
    } catch (err) {
      onError(errorMessage(err, 'Backfill failed.'));
    } finally {
      setBackfilling(false);
    }
  }, [source.id, refresh, onError]);

  const onRefreshMetadata = useCallback(async () => {
    setRefreshingMetadata(true);
    try {
      const res = await apiPost<RefreshMetadataResponse>(
        `/api/v1/admin/confluence/sources/${source.id}/refresh-metadata`,
        {},
      );
      const errPart =
        res.errors.length > 0
          ? ` (${res.errors.length} error${res.errors.length === 1 ? '' : 's'})`
          : '';
      alert(
        `Refresh complete: ${res.updated} updated, ${res.skippedUnchanged} skipped, ${res.notIngested} not yet ingested${errPart}.`,
      );
      refresh();
    } catch (err) {
      onError(errorMessage(err, 'Refresh failed.'));
    } finally {
      setRefreshingMetadata(false);
    }
  }, [source.id, refresh, onError]);

  if (loading && pages === null) {
    return <div className="py-3 text-xs text-nebula-fg-4">Loading pages…</div>;
  }

  const pagesAttempted = source.lastSyncStats?.pagesSucceeded ?? 0;
  const showBackfill = pages !== null && pages.length === 0 && pagesAttempted > 0;

  if (showBackfill) {
    return (
      <div className="rounded-md border border-nebula-warning/20 bg-nebula-warning-bg px-4 py-3 text-sm">
        <p className="text-nebula-fg-1">
          This source has {pagesAttempted} synced pages from before per-page tracking. Refresh
          metadata to pull the latest title + author + source link from Confluence (no
          re-embedding).
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRefreshMetadata}
            disabled={refreshingMetadata}
            className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-nebula-surface px-3 py-1.5 text-xs font-semibold text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
          >
            <RefreshCwIcon size={12} className={refreshingMetadata ? 'animate-spin' : ''} />
            {refreshingMetadata ? 'Refreshing…' : 'Refresh metadata from Confluence'}
          </button>
          <button
            type="button"
            onClick={onBackfill}
            disabled={backfilling}
            title="Legacy: only stamps source link, doesn't fix title/author"
            className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-nebula-surface px-3 py-1.5 text-xs font-semibold text-nebula-fg-3 hover:bg-nebula-tint disabled:opacity-60"
          >
            {backfilling ? 'Backfilling…' : 'Backfill source link only'}
          </button>
        </div>
      </div>
    );
  }

  if (pages !== null && pages.length === 0) {
    return (
      <div className="py-3 text-xs text-nebula-fg-4">
        No pages from this source yet. Run <strong>Sync</strong> to ingest the subtree.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onRefreshMetadata}
          disabled={refreshingMetadata}
          title="Pull the latest title + author from Confluence for every synced page. Does not re-embed."
          className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-nebula-surface px-2.5 py-1 text-[11px] font-semibold text-nebula-secondary hover:bg-nebula-tint disabled:opacity-60"
        >
          <RefreshCwIcon size={11} className={refreshingMetadata ? 'animate-spin' : ''} />
          {refreshingMetadata ? 'Refreshing metadata…' : 'Refresh metadata from Confluence'}
        </button>
      </div>
      <div className="overflow-hidden rounded-md border border-nebula-border-soft">
        <table className="w-full text-sm">
          <thead className="bg-nebula-gray-5 text-left text-[11px] uppercase tracking-wider text-nebula-fg-4">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Chunks</th>
              <th className="px-3 py-2">Last indexed</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-nebula-border-soft">
            {(pages ?? []).map((p) => {
              const pageId = pageIdFromFilename(p.filename);
              const title = p.documentTitle || titleFromFilename(p.filename);
              const ringChip =
                p.ring === 'ring0' ? (
                  <span className="ml-2 inline-flex items-center rounded bg-nebula-gray-5 px-1.5 py-0.5 text-[10px] font-semibold text-nebula-fg-1">
                    OneNebula Internal
                  </span>
                ) : null;
              return (
                <tr key={p.filename}>
                  <td className="px-3 py-2 text-nebula-fg-1">
                    {pageId ? (
                      <a
                        href={confluenceUrl(source.cloudBaseUrl, pageId)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-nebula-secondary hover:underline"
                      >
                        {title}
                        <ExternalLinkIcon size={11} />
                      </a>
                    ) : (
                      <span className="font-medium">{title}</span>
                    )}
                    {ringChip}
                  </td>
                  <td className="px-3 py-2 text-nebula-fg-3">
                    {(p.chunkCount ?? 0) > 0 ? (
                      <button
                        type="button"
                        onClick={() => setInspectChunks(p)}
                        title="View chunk text"
                        className="rounded px-1.5 py-0.5 font-medium text-nebula-secondary hover:bg-nebula-tint hover:underline"
                      >
                        {p.chunkCount}
                      </button>
                    ) : (
                      (p.chunkCount ?? 0)
                    )}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-nebula-fg-3">
                    {formatDate(p.lastIngestedAt)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onReindex(p.filename)}
                        disabled={reindexing[p.filename] || deleting[p.filename]}
                        className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border px-2 py-1 text-[11px] font-semibold text-nebula-secondary hover:bg-nebula-tint disabled:opacity-60"
                      >
                        <RefreshCwIcon
                          size={11}
                          className={reindexing[p.filename] ? 'animate-spin' : ''}
                        />
                        {reindexing[p.filename] ? 'Re-syncing…' : 'Re-sync'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete({ filename: p.filename, title })}
                        disabled={deleting[p.filename] || reindexing[p.filename]}
                        title="Delete this page from the knowledgebase"
                        aria-label={`Delete ${title}`}
                        className="inline-flex items-center justify-center rounded-md border border-nebula-error/30 px-2 py-1 text-nebula-error hover:bg-nebula-error-bg disabled:opacity-60"
                      >
                        <Trash2Icon size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan={4}
                className="border-t border-nebula-border-soft bg-nebula-gray-5 px-3 py-2 text-right text-[11px] text-nebula-fg-4"
              >
                Total pages: {pages?.length ?? 0} • Total chunks: {totalChunks}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {blockedPages.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-nebula-fg-4">
            Blocked from re-ingest ({blockedPages.length})
          </div>
          <div className="overflow-hidden rounded-md border border-nebula-warning/20">
            <table className="w-full text-sm">
              <thead className="bg-nebula-warning-bg text-left text-[11px] uppercase tracking-wider text-nebula-fg-4">
                <tr>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Blocked at</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nebula-border-soft">
                {blockedPages.map((p) => {
                  const pageId = pageIdFromFilename(p.filename);
                  const title = p.documentTitle || titleFromFilename(p.filename);
                  return (
                    <tr key={p.filename}>
                      <td className="px-3 py-2 text-nebula-fg-1">
                        {pageId ? (
                          <a
                            href={confluenceUrl(source.cloudBaseUrl, pageId)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-medium text-nebula-secondary hover:underline"
                          >
                            {title}
                            <ExternalLinkIcon size={11} />
                          </a>
                        ) : (
                          <span className="font-medium">{title}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[12px] text-nebula-fg-3">
                        {formatDate(p.lastIngestedAt)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => onUnblock(p.filename)}
                          disabled={unblocking[p.filename]}
                          className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-nebula-surface px-2 py-1 text-[11px] font-semibold text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
                        >
                          {unblocking[p.filename] ? 'Unblocking…' : 'Unblock'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pendingDelete && (
        <DeletePageModal
          filename={pendingDelete.filename}
          title={pendingDelete.title}
          deleting={!!deleting[pendingDelete.filename]}
          onCancel={() => setPendingDelete(null)}
          onConfirm={(blockFromIngest) => performDelete(pendingDelete.filename, blockFromIngest)}
        />
      )}

      {inspectChunks && (
        <ConfluenceChunksModal doc={inspectChunks} onClose={() => setInspectChunks(null)} />
      )}
    </div>
  );
}

// ---------- ConfluenceChunksModal -------------------------------------------
// Same shape as AdminKnowledgebase's ChunksModal, but renders chunk text
// through react-markdown with the same `attachment://hash` resolver the
// Knowledge Agent uses, so embedded screenshots + fenced code blocks display
// nicely. Read-only spot-check view.

interface ChunkRow {
  chunkId: string;
  chunkIndex: number;
  page: number | null;
  chunkText: string;
  sectionType: string | null;
  documentSection: string | null;
}

function resolveAttachmentUrl(href?: string): string {
  if (!href) return '';
  if (href.startsWith('attachment://')) {
    const hash = href.slice('attachment://'.length);
    if (/^[a-f0-9]{64}$/.test(hash)) return `/api/v1/attachments/${hash}`;
  }
  return href;
}

function ConfluenceChunksModal({
  doc,
  onClose,
}: {
  doc: DocumentRegistryEntry;
  onClose: () => void;
}): JSX.Element {
  const [chunks, setChunks] = useState<ChunkRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ filename: string; chunks: ChunkRow[] }>(
      `/api/v1/admin/knowledgebase/documents/${encodeURIComponent(doc.filename)}/chunks`,
    )
      .then((res) => {
        if (!cancelled) setChunks(res.chunks);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load chunks.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [doc.filename]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cf-chunks-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/50 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-nebula-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-nebula-border-soft px-6 py-4">
          <div>
            <h2 id="cf-chunks-title" className="text-lg font-semibold text-nebula-fg-1">
              Chunks of {doc.documentTitle || doc.filename}
            </h2>
            <p className="mt-1 text-xs text-nebula-fg-3">{doc.chunkCount} chunks indexed</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1"
          >
            <XIcon size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!chunks && !error && (
            <p className="py-10 text-center text-sm text-nebula-fg-3">Loading chunks…</p>
          )}
          {error && (
            <p className="rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
              {error}
            </p>
          )}
          {chunks && chunks.length === 0 && !error && (
            <p className="py-10 text-center text-sm text-nebula-fg-3">
              No chunks found for this document.
            </p>
          )}
          {chunks && chunks.length > 0 && (
            <ol className="space-y-3">
              {chunks.map((c) => (
                <li
                  key={c.chunkId}
                  className="rounded-lg border border-nebula-border-soft bg-nebula-gray-5 px-4 py-3"
                >
                  <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-nebula-fg-3">
                    <span>chunk {c.chunkIndex + 1}</span>
                    {c.page !== null && <span>· page {c.page}</span>}
                    {c.sectionType && <span>· {c.sectionType}</span>}
                    {c.documentSection && <span>· {c.documentSection}</span>}
                  </div>
                  <div className="prose prose-sm max-w-none text-sm text-nebula-fg-1">
                    <ReactMarkdown
                      // urlTransform default in react-markdown v9 strips
                      // unknown URL schemes (attachment://). Resolve to
                      // the proxy URL up front so the safelist allows it.
                      urlTransform={(uri: string) => resolveAttachmentUrl(uri)}
                      components={{
                        code: CodeBlock,
                        img: ({ src, alt }) => (
                          <img
                            src={typeof src === 'string' ? src : ''}
                            alt={alt ?? ''}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }}
                          />
                        ),
                        a: ({ href, children }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {c.chunkText}
                    </ReactMarkdown>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="border-t border-nebula-border-soft bg-nebula-gray-5 px-6 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-nebula-border px-4 py-1.5 text-sm text-nebula-fg-1 transition-colors hover:bg-nebula-tint"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface DeletePageModalProps {
  filename: string;
  title: string;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: (blockFromIngest: boolean) => void;
}

function DeletePageModal({
  title,
  deleting,
  onCancel,
  onConfirm,
}: DeletePageModalProps): JSX.Element {
  const [blockFromIngest, setBlockFromIngest] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-nebula-surface p-6 shadow-lg">
        <h2 className="text-lg font-bold text-nebula-fg-1">Delete &quot;{title}&quot;</h2>
        <p className="mt-2 text-sm text-nebula-fg-3">
          Choose how the next sync should treat this page.
        </p>
        <div className="mt-4 space-y-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="delete-mode"
              checked={!blockFromIngest}
              onChange={() => setBlockFromIngest(false)}
              className="mt-1"
            />
            <span>
              <span className="font-semibold text-nebula-fg-1">Delete this page</span>
              <span className="block text-nebula-fg-3">
                Removes from the knowledgebase. The next sync will re-ingest it from Confluence if
                it&apos;s still there.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="delete-mode"
              checked={blockFromIngest}
              onChange={() => setBlockFromIngest(true)}
              className="mt-1"
            />
            <span>
              <span className="font-semibold text-nebula-fg-1">
                Delete and block from re-ingest
              </span>
              <span className="block text-nebula-fg-3">
                Permanently excludes this page from the knowledgebase. Future syncs will skip it.
                You can unblock it later from this panel.
              </span>
            </span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-nebula-border px-3 py-2 text-sm font-semibold text-nebula-fg-3 hover:bg-nebula-tint"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(blockFromIngest)}
            disabled={deleting}
            className="rounded-md bg-nebula-error px-4 py-2 font-ui text-white hover:bg-nebula-error/90 disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
